import { extensionOf, isLosslessExtension } from "./audio/metadata";
import type { AnalysisReport, AnalysisResult, Finding, Verdict } from "./types";
import { formatKhz, formatTime } from "./utils/format";

interface ReportInput {
  analysis: AnalysisResult;
  file: File;
  duration: number;
  sampleRate: number;
}

export function buildReport({
  analysis,
  file,
  duration,
  sampleRate,
}: ReportInput): AnalysisReport {
  const { cutoff, stats, discontinuities } = analysis;
  const extension = extensionOf(file.name);
  const isLossless = isLosslessExtension(extension);
  const nyquist = sampleRate / 2;
  const cutoffKhz = cutoff.hz / 1000;
  const suspiciousCutoff = cutoff.sharp && cutoff.hz < nyquist * 0.92 && cutoff.hz >= 11000;
  const veryLowCutoff = suspiciousCutoff && cutoff.hz < 17500;
  const heavyCompression = stats.crestDb < 6.5 || stats.loudnessRange < 4;
  const clipping = stats.clippingPercent > 0.003;
  const bitrate = (file.size * 8) / duration / 1000;
  const findings: Finding[] = [];
  let score = 0;

  if (suspiciousCutoff) {
    const typical = nearestCodecBand(cutoffKhz);
    findings.push({
      severity: isLossless ? "danger" : "warning",
      title: `Queda espectral em ${formatKhz(cutoff.hz)}`,
      text: `A energia cai ${cutoff.dropDb.toFixed(1)} dB de forma persistente${typical ? `, próxima da faixa comum de ${typical}` : ""}.`,
    });
    score += veryLowCutoff ? 48 : 34;
  } else {
    findings.push({
      severity: "good",
      title: "Sem cutoff lossy evidente",
      text: `Há conteúdo útil até aproximadamente ${formatKhz(cutoff.hz)} dentro do limite de ${formatKhz(nyquist)}.`,
    });
  }

  if (isLossless && suspiciousCutoff) {
    findings.push({
      severity: "danger",
      title: "Container lossless, assinatura possivelmente lossy",
      text: `A extensão .${extension} não recupera frequências descartadas por uma compressão anterior. Este é o principal indício de transcode.`,
    });
    score += 28;
  } else if (isLossless) {
    findings.push({
      severity: "good",
      title: "Container e espectro são coerentes",
      text: "Não há uma incompatibilidade forte entre o formato declarado e a distribuição espectral.",
    });
  } else {
    findings.push({
      severity: "info",
      title: `Formato com perdas (.${extension || "áudio"})`,
      text: "Algum cutoff pode ser comportamento esperado do próprio codec e não sinal de falsificação.",
    });
  }

  if (clipping) {
    findings.push({
      severity: "warning",
      title: "Clipping digital detectado",
      text: `${stats.clipped.toLocaleString("pt-BR")} amostras (${stats.clippingPercent.toFixed(4)}%) chegaram ao limite digital.`,
    });
    score += 7;
  } else {
    findings.push({
      severity: "good",
      title: "Sem clipping relevante",
      text: `Pico em ${stats.peakDb.toFixed(2)} dBFS; não há concentração significativa de amostras saturadas.`,
    });
  }

  findings.push(
    heavyCompression
      ? {
          severity: "warning",
          title: "Dinâmica possivelmente comprimida",
          text: `Crest factor de ${stats.crestDb.toFixed(1)} dB e variação de loudness de ${stats.loudnessRange.toFixed(1)} dB sugerem masterização densa.`,
        }
      : {
          severity: "good",
          title: "Dinâmica preservada",
          text: `Crest factor de ${stats.crestDb.toFixed(1)} dB e variação de ${stats.loudnessRange.toFixed(1)} dB.`,
        },
  );
  if (heavyCompression) score += 5;

  if (discontinuities.length) {
    const times = discontinuities.slice(0, 4).map(({ time }) => formatTime(time)).join(", ");
    findings.push({
      severity: "warning",
      title: `${discontinuities.length} possível(is) descontinuidade(s)`,
      text: `Saltos de amostra acima do padrão em ${times}${discontinuities.length > 4 ? "…" : ""}. Podem ser edição ou transientes musicais.`,
    });
    score += Math.min(8, discontinuities.length);
  } else {
    findings.push({
      severity: "good",
      title: "Nenhum corte abrupto óbvio",
      text: "A varredura de descontinuidades não encontrou saltos isolados fortes.",
    });
  }

  score = Math.min(98, score);
  let verdict: Verdict;
  if (isLossless && suspiciousCutoff && score >= 65) {
    verdict = {
      level: "danger",
      icon: "!",
      title: "Provável falso lossless",
      subtitle: "Há assinatura compatível com fonte lossy.",
      confidence: Math.round(Math.max(62, Math.min(94, 56 + cutoff.confidence * 32))),
      note: "Indício forte, não prova matemática: gravações, filtros e masters antigos também podem limitar a banda.",
    };
  } else if (score >= 35) {
    verdict = {
      level: "warning",
      icon: "~",
      title: "Arquivo suspeito",
      subtitle: "Há sinais que merecem inspeção visual.",
      confidence: Math.round(Math.max(48, Math.min(82, 44 + cutoff.confidence * 30))),
      note: "Compare com uma fonte conhecida e observe se a linha de cutoff atravessa trechos musicais diferentes.",
    };
  } else {
    verdict = {
      level: "good",
      icon: "✓",
      title: "Nenhum indício forte",
      subtitle: "Espectro coerente com o arquivo analisado.",
      confidence: isLossless ? 72 : 64,
      note: "Isto não certifica a procedência. Transcodes em bitrate alto podem ser indistinguíveis sem a fonte original.",
    };
  }

  return { verdict, findings, bitrate, cutoff, stats };
}

function nearestCodecBand(khz: number): string | null {
  const bands: Array<[number, string]> = [
    [15.5, "MP3/AAC de bitrate baixo"],
    [16, "MP3 128–160 kbps"],
    [19, "MP3 192 kbps"],
    [20, "MP3/AAC de bitrate alto"],
  ];
  const nearest = bands.reduce((left, right) =>
    Math.abs(right[0] - khz) < Math.abs(left[0] - khz) ? right : left,
  );
  return Math.abs(nearest[0] - khz) <= 1.1 ? nearest[1] : null;
}
